import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

interface DrawerProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  children: React.ReactNode
  placement?: 'left' | 'right' | 'top' | 'bottom'
  hideCloseButton?: boolean
  radius?: 'none' | 'sm' | 'md' | 'lg'
  classNames?: {
    base?: string
    backdrop?: string
    header?: string
    body?: string
    footer?: string
  }
  className?: string
}

interface DrawerContentProps {
  children: React.ReactNode
  className?: string
}

interface DrawerHeaderProps {
  children: React.ReactNode
  className?: string
}

interface DrawerBodyProps {
  children: React.ReactNode
  className?: string
}

interface DrawerFooterProps {
  children: React.ReactNode
  className?: string
}

export function Drawer({
  isOpen,
  onOpenChange,
  children,
  placement = 'right',
  hideCloseButton = false,
  radius = 'none',
  classNames = {},
  className = ''
}: DrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null)

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onOpenChange(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onOpenChange])

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onOpenChange(false)
    }
  }

  // Check if custom width is provided in classNames.base
  const hasCustomWidth = classNames.base?.includes('w-[') || classNames.base?.includes('w-')

  // Default width - 424px, can be overridden by classNames.base
  const defaultWidth = 'w-[424px]'

  // Placement classes
  const placementClasses = {
    left: {
      container: 'justify-start',
      drawer: 'h-full',
      transform: isOpen ? 'translate-x-0' : '-translate-x-full'
    },
    right: {
      container: 'justify-end',
      drawer: 'h-full',
      transform: isOpen ? 'translate-x-0' : 'translate-x-full'
    },
    top: {
      container: 'items-start',
      drawer: 'w-full',
      transform: isOpen ? 'translate-y-0' : '-translate-y-full'
    },
    bottom: {
      container: 'items-end',
      drawer: 'w-full',
      transform: isOpen ? 'translate-y-0' : 'translate-y-full'
    }
  }

  // Radius classes
  const radiusClasses = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg'
  }

  // Clone children and pass classNames to them
  const enhanceChildren = (children: React.ReactNode): React.ReactNode => {
    return React.Children.map(children, child => {
      if (React.isValidElement(child)) {
        // Check if it's one of our drawer components
        if (child.type === DrawerContent) {
          return React.cloneElement(child, {
            ...child.props,
            children: enhanceChildren(child.props.children)
          })
        } else if (child.type === DrawerHeader) {
          return React.cloneElement(child, {
            ...child.props,
            className: `${child.props.className || ''} ${classNames.header || ''}`.trim()
          })
        } else if (child.type === DrawerBody) {
          return React.cloneElement(child, {
            ...child.props,
            className: `${child.props.className || ''} ${classNames.body || ''}`.trim()
          })
        } else if (child.type === DrawerFooter) {
          return React.cloneElement(child, {
            ...child.props,
            className: `${child.props.className || ''} ${classNames.footer || ''}`.trim()
          })
        } else if (child.props?.children) {
          return React.cloneElement(child, {
            ...child.props,
            children: enhanceChildren(child.props.children)
          })
        }
      }
      return child
    })
  }

  if (!isOpen) return null

  const drawerElement = (
    <div
      className={`fixed inset-0 z-[1050] flex ${placementClasses[placement].container} ${classNames.backdrop || ''}`}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleBackdropClick}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`
          relative bg-white shadow-popover transition-transform duration-300 ease-in-out z-10
          ${(placement === 'left' || placement === 'right') && !hasCustomWidth ? defaultWidth : ''}
          ${placementClasses[placement].drawer}
          ${radiusClasses[radius]}
          ${placementClasses[placement].transform}
          ${classNames.base || ''}
          ${className}
        `}
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        {!hideCloseButton && (
          <button
            onClick={() => onOpenChange(false)}
            className='absolute top-3 right-6 z-10 p-2 rounded-lg'
            aria-label='Close drawer'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
            >
              <path
                d='M10.5839 12.5001L4.927 6.84317L6.34122 5.42896L11.9981 11.0859L17.655 5.42896L19.0692 6.84317L13.4123 12.5001L19.0691 18.1569L17.6549 19.5711L11.9981 13.9143L6.34132 19.5711L4.92711 18.1569L10.5839 12.5001Z'
                fill='#161513'
              />
            </svg>
          </button>
        )}

        {enhanceChildren(children)}
      </div>
    </div>
  )

  return createPortal(drawerElement, document.body)
}

export function DrawerContent({ children, className = '' }: DrawerContentProps) {
  return <div className={`flex flex-col h-full ${className}`}>{children}</div>
}

export function DrawerHeader({ children, className = '' }: DrawerHeaderProps) {
  return (
    <div className={`flex-shrink-0 p-6 border-b border-[#EAEAEA] h-[65px] ${className}`}>
      {children}
    </div>
  )
}

export function DrawerBody({ children, className = '' }: DrawerBodyProps) {
  return <div className={`flex-1 p-6 overflow-y-auto ${className}`}>{children}</div>
}

export function DrawerFooter({ children, className = '' }: DrawerFooterProps) {
  return <div className={`flex-shrink-0 px-6 pt-6 pb-12 ${className}`}>{children}</div>
}

export default Drawer
